import { create } from 'zustand';

import { logout as apiLogout } from '../api/auth';
import { API_BASE_URL, AUTH_BASE_URL } from '../api/config';
import { describeAuthError } from '../api/errors';
import { fetchUser, OrganizationInactiveError } from '../api/user';
import type { User } from '../types/user';

import { hubspotResetIdentity } from '@/shared/analytics/hubspot';
import { isAnalyticsEnabled, posthog } from '@/shared/analytics/posthog';
import { IS_MOCK_SESSION } from '@/shared/dev/isMockSession';
import { track } from '@/shared/analytics/track';
import { isAppPath, needsAuthCheck } from '@/shared/routes/paths';

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isError: boolean;
  /**
   * Set when the API rejected the session because the organization has been
   * deactivated. Distinct from `isError`/`user: null` so the UI can explain
   * "your access ended" instead of silently showing the login screen.
   */
  inactiveOrgMessage: string | null;
  /**
   * Whether the session question has been answered at all — either the probe
   * completed, or we deliberately skipped it on a public route.
   *
   * Without this, `user: null, isLoading: false` means both "no session" and
   * "haven't looked yet", and analytics cannot tell an anonymous visitor apart
   * from one whose probe is still in flight.
   */
  hasCheckedAuth: boolean;
  login: (user: User) => void;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

/**
 * On a public route there is nothing to probe, so the session question is
 * already answered before React mounts.
 *
 * Resolved at store creation rather than in an App effect on purpose: React
 * runs child effects before the parent's, so an effect-based skip would let the
 * landing hero's first funnel events fire while the analytics snapshot still
 * said 'unknown' — burying the site's primary conversion funnel in an
 * unresolved bucket.
 */
const publicRouteOnBoot = (): boolean =>
  typeof window !== 'undefined' && !needsAuthCheck(window.location.pathname);

export const useAuth = create<AuthState>()((set, get) => ({
  user: null,
  isLoading: false,
  isError: false,
  inactiveOrgMessage: null,
  hasCheckedAuth: publicRouteOnBoot(),
  // Not a login: the only caller is useOrganizationSwitch, which already emits
  // `organization_switched`. It used to fire `auth_login_succeeded` here, which
  // reported every org switch as a fresh sign-in.
  login: (user: User) => {
    set({
      user,
      isLoading: false,
      isError: false,
      inactiveOrgMessage: null,
      hasCheckedAuth: true
    });
  },
  logout: async () => {
    track('auth_logout');
    await apiLogout();
    set({
      user: null,
      isLoading: false,
      isError: false,
      inactiveOrgMessage: null
    });
    if (isAnalyticsEnabled) {
      posthog.reset();
    }
    // Symmetric with posthog.reset(): both analytics identities are dropped so
    // the next person on a shared machine starts anonymous rather than
    // inheriting this session's contact.
    hubspotResetIdentity();
  },
  /**
   * Restores an existing session. This is a probe, not a login attempt — it
   * runs for every visitor, including anonymous traffic on the public landing
   * page — so its outcomes are reported as `auth_session_*`. The success event
   * lives in AnalyticsBootstrap instead, because it has to be captured after
   * `posthog.identify()` to attach to the person.
   */
  checkAuth: async () => {
    const { isLoading } = get();
    if (isLoading) return;

    // Local mock session — see src/shared/dev/isMockSession.ts. In a production
    // build IS_MOCK_SESSION is the literal `false`, so this branch and the
    // dynamic import below are removed by the bundler and the mock user is
    // never shipped. Do not make this a static import.
    if (IS_MOCK_SESSION) {
      const { MOCK_USER } = await import('@/shared/dev/mockSession');
      set({
        user: MOCK_USER,
        isLoading: false,
        isError: false,
        hasCheckedAuth: true,
        inactiveOrgMessage: null
      });
      return;
    }

    const startedAt = Date.now();
    const isAppRoute =
      typeof window !== 'undefined' && isAppPath(window.location.pathname);

    try {
      set({ isLoading: true, isError: false, inactiveOrgMessage: null });
      const user = await fetchUser();
      set({ user, isLoading: false, isError: false, hasCheckedAuth: true });

      // `user` is null whenever there is no session; reading `.id` off it threw
      // and landed in the catch below, reporting a real logged-out state as an
      // auth failure.
      if (user === null) {
        track('auth_session_absent', {
          is_app_route: isAppRoute,
          duration_ms: Date.now() - startedAt
        });
      }
    } catch (error) {
      console.error('Auth check failed:', error);

      track('auth_session_restore_failed', {
        ...describeAuthError(error),
        duration_ms: Date.now() - startedAt,
        is_app_route: isAppRoute,
        // If network_error correlates with this being true, the cross-origin
        // /auth/me call is the culprit rather than the auth service itself.
        auth_host_differs: AUTH_BASE_URL !== API_BASE_URL
      });

      set({
        user: null,
        isLoading: false,
        isError: true,
        hasCheckedAuth: true,
        inactiveOrgMessage:
          error instanceof OrganizationInactiveError ? error.message : null
      });
    }
  }
}));
